import { AppPhase, DealingSubPhase } from '../models/communication/app-enums.model';
import { CardDealtEvent } from '../models/communication/app-events.model';

/**
 * An abstract class for all the main game flows (`open-game-flow`, `classic-game-flow`…)
 *
 * Exposes an API for a simple state machine.
 * `state` is defined by the union of possible states (string), e.g.
 * 
 * `type States = '1-START' | '2-DRAW-AND-PLACE-CARD' | '3-OUTCOME';`
 
 * `game` is a reference to the main game class.
 * The `Game` type parameter prevents circular dependencies.
 */

export abstract class GameFlowBase<Game, State = string> {
    protected readonly states: readonly State[] = [];
    protected readonly game!: Game;

    private readonly log: boolean = true;
    private stateIndex = 0;

    constructor(game: Game) {
        this.game = game;
    }

    nextState(): void {
        this.moveToState(this.getState(this.stateIndex + 1));
    }

    /**
     * Sets the next `stateIndex` and logs the transition.
     */
    moveToState(newState: State): void {
        if (newState === undefined) {
            throw new Error('Invalid state');
        }

        const previousState = this.getState();
        this.stateIndex = this.states.indexOf(newState);

        if (this.log) {
            // eslint-disable-next-line no-console
            console.log(`Move from state: ${String(previousState)} -> ${String(this.getState())}`);
        }
    }

    getState(stateIndex = this.stateIndex): State {
        return this.states[stateIndex];
    }

    /**
     * An abstract method expected to be implemented using a simple state machine.
     * The method triggers when the relevant app events are sent.
     *
     * `CardDealtEvent` contains the relevant updates about the table state,
     * while `AppPhase` and `DealingSubPhase` signal the phase changes.
     */
    abstract onChanges(table?: CardDealtEvent, subPhase?: DealingSubPhase, appPhase?: AppPhase): void;
}
